// Keyboard focus containment for the portal filter drawers (WCAG 2.4.3,
// finding IGG1-F46). QDrawer has no built-in focus handling: while the drawer
// is open this keeps Tab/Shift+Tab inside it, closes it on Escape and returns
// focus to the trigger button when it closes.
import { watch, nextTick, onBeforeUnmount } from "vue";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

export default function useDrawerFocusTrap(
  isOpen,
  drawerRef,
  onClose,
  returnFocusSelector
) {
  let trapEl = null;
  let returnEl = null;

  const getFocusable = () =>
    Array.from(trapEl.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      // skips controls inside collapsed q-expansion-item panels (display: none)
      (el) => el.offsetParent !== null
    );

  const onKeydown = (e) => {
    if (e.key === "Escape") {
      onClose();
      return;
    }
    if (
      e.key !== "Tab" ||
      e.defaultPrevented ||
      e.ctrlKey ||
      e.altKey ||
      e.metaKey
    ) {
      return;
    }
    // Take over Tab stepping entirely while the drawer is open: stepping from
    // our own list (instead of only wrapping at the first/last element) means
    // focus can never cross the drawer boundary even when it sits on an
    // element outside the list.
    const focusable = getFocusable();
    e.preventDefault();
    if (focusable.length === 0) {
      return;
    }
    const idx = focusable.indexOf(e.target);
    let next;
    if (idx !== -1) {
      next = e.shiftKey
        ? focusable[(idx - 1 + focusable.length) % focusable.length]
        : focusable[(idx + 1) % focusable.length];
    } else {
      // Focus sits on the container or a tabindex="-1" node - Quasar parks
      // focus on a .q-focus-helper child after mouse clicks on q-item headers
      // and q-btn. Step from its DOM position, like native Tab would.
      let before = null;
      let after = null;
      for (const el of focusable) {
        const pos = e.target.compareDocumentPosition(el);
        if ((pos & Node.DOCUMENT_POSITION_PRECEDING) !== 0) {
          before = el; // list is in document order; ends as closest preceding
        } else if (
          after === null &&
          (pos & Node.DOCUMENT_POSITION_FOLLOWING) !== 0
        ) {
          after = el;
        }
      }
      next = e.shiftKey
        ? before || focusable[focusable.length - 1]
        : after || focusable[0];
    }
    next.focus();
  };

  const activate = async () => {
    returnEl =
      document.activeElement === document.body ? null : document.activeElement;
    await nextTick();
    const root = drawerRef.value && drawerRef.value.$el;
    trapEl = root ? root.querySelector(".q-drawer__content") : null;
    if (!trapEl) {
      return;
    }
    trapEl.addEventListener("keydown", onKeydown);
    trapEl.focus();
  };

  const deactivate = () => {
    if (!trapEl) {
      return;
    }
    trapEl.removeEventListener("keydown", onKeydown);
    // restore focus only when it is still inside the drawer (Escape, close
    // button) or already lost to <body> (click on the blurred area); when the
    // user clicked an interactive element elsewhere, leave focus there
    const shouldRestore =
      trapEl.contains(document.activeElement) ||
      document.activeElement === document.body;
    trapEl = null;
    const target =
      returnEl && returnEl.isConnected
        ? returnEl
        : document.querySelector(returnFocusSelector);
    returnEl = null;
    if (shouldRestore && target) {
      target.focus();
    }
  };

  watch(isOpen, (open) => (open ? activate() : deactivate()));
  onBeforeUnmount(deactivate);
}
